import type { ReactNode } from 'react'
import clsx from 'clsx'
import Info from '../../icons/Info'
import GradientWrapper from '../misc/Gradient'

export type ChipInfoTone = 'viq' | 'special' | 'yellow' | 'red' | 'green'

export type ChipInfoProps = {
  label: string
  tone: ChipInfoTone
  size?: 'small' | 'medium' | 'large'
  icon?: ReactNode
  onClick?: () => void
  className?: string
}

const sizeStyles = {
  small: { chip: 'h-4 px-[5.5px]', text: 'font-8-bold', icon: '10.5' },
  medium: { chip: 'h-6 px-2', text: 'font-12-bold', icon: '13.5' },
  large: { chip: 'h-8 px-3', text: 'font-16-bold leading-[125%] tracking-[0%]', icon: '16' },
} as const

const toneStyles: Record<ChipInfoTone, { chip: string; gradient: string }> = {
  viq: { chip: 'bg-red-500 text-red-900', gradient: 'bg-gradient-light-elevation-linear' },
  special: { chip: 'bg-red-550 text-red-900', gradient: 'bg-gradient-light-elevation-linear-matt' },
  yellow: { chip: 'bg-gold text-red-900', gradient: 'bg-gradient-light-elevation-linear-matt' },
  red: { chip: 'bg-red-500 text-red-900', gradient: 'bg-gradient-light-elevation-linear-matt' },
  green: {
    chip: 'bg-green-400 text-green-800',
    gradient: 'bg-gradient-light-elevation-linear-matt',
  },
}

export default function ChipInfo({
  label,
  tone,
  size = 'small',
  icon,
  onClick,
  className,
}: ChipInfoProps) {
  const sizeStyle = sizeStyles[size]
  const toneStyle = toneStyles[tone]
  const clickable = !!onClick

  const content = (
    <>
      <GradientWrapper gradient={toneStyle.gradient} rounded="rounded-full" />
      {clickable && (
        <GradientWrapper
          gradient="group-hover:bg-white/35 group-active:bg-white/35"
          rounded="rounded-full"
        />
      )}
      <span
        className={clsx('relative z-10 flex items-center justify-center gap-1', sizeStyle.text)}
      >
        {icon && (
          <span aria-hidden className="flex">
            {icon}
          </span>
        )}
        <span>{label}</span>
        {clickable && (
          <span aria-hidden className="flex">
            <Info width={sizeStyle.icon} height={sizeStyle.icon} />
          </span>
        )}
      </span>
    </>
  )

  const chipClassName = clsx(
    'font-12-bold relative inline-flex items-center justify-center rounded-full align-middle',
    sizeStyle.chip,
    toneStyle.chip,
    className,
  )

  if (!clickable) return <span className={chipClassName}>{content}</span>

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={`${label}, Info öffnen`}
      onClick={onClick}
      className={clsx(
        'group cursor-pointer',
        "after:absolute after:-inset-1 after:content-['']",
        chipClassName,
      )}
    >
      {content}
    </button>
  )
}
